// Owner Contracts: the owner agreements waiting to be released, with the FICA
// pack beside each one (db/migrations/1300).
//
// Why it exists. On 1 October 2026 an owner completed the asset finance
// contract form and nobody could see his documents: the form's files existed
// only as Typeform links, which do not open without the vault's API token, and
// no screen released the agreement at all. Now the files are copied into the
// owner-onboarding bucket as the form lands, and this page shows each one with
// View, Accept and Reject, the form's answers to check them against, and the
// Release button.
//
// What decides what. Postgres says what still blocks a release
// (owner_contract_release_blockers_v1) and refuses a release that is not ready;
// this page shows those sentences and never works them out itself. CLAUDE.md
// rule 4. Releasing sends the SignNow agreement through tail-webhooks; a send
// that fails puts the gate back to waiting and the reason shows here.
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, Check, ChevronDown, ChevronRight, FileSignature, RefreshCw, Send, ShieldAlert, X } from 'lucide-react'
import { useDecideOwnerContract, useFetchOwnerFormFiles, useGetOwnerContracts, useReviewOwnerDocument } from '../hooks/backend/owners'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { Spinner } from '../lib/shadcn/spinner'
import { toast } from '../lib/shadcn/sonner'
import { Chip, type ChipTone } from '../components/interviews/Chip'
import { OwnerDocUpload, ReadingView, ViewButton, type Upload } from '../components/owners/OwnerPipelineDialog'
import { formatDay, formatWhen, localPhone } from '../utils/loans'
import { writeErrorMessage } from '../utils/writeError'

type FormFile = {
  import_id: string
  form_field: string
  doc_kind: string
  label: string
  status: 'pending' | 'imported' | 'failed' | 'unsupported'
  error: string | null
  attempts: number
  document_id: string | null
  last_attempt_at: string | null
}

type Doc = Upload & { mime_type: string; same_file_as: string[] | null }

type Release = {
  release_id: string
  status: 'awaiting_release' | 'released' | 'declined'
  created_at: string
  notes: string | null
  released_at: string | null
  released_by: string | null
  declined_at: string | null
  declined_by: string | null
  decline_reason: string | null
  envelope_id: string | null
  signed_at: string | null
  last_send_error: string | null
  application_id: string
  onboarding_detail_id: string
  application_status: string
  owner_model: string
  name: string | null
  email: string | null
  phone: string | null
  fica_complete: boolean | null
  fica_missing: string[] | null
  form_submitted_at: string | null
  details: Record<string, string | boolean | null>
  form_files: FormFile[]
  form_files_unseen: number
  documents: Doc[]
  release_blockers: string[]
}

type Queue = { counts: { awaiting: number; released_30d: number }; rows: Release[] }

const MODEL_LABEL: Record<string, string> = {
  asset_finance: 'Asset finance, R200,000 programme',
  brings_own_vehicle: 'Brings own vehicle',
  brought_in: 'Brought in',
}

const STATE: Record<string, { label: string; tone: ChipTone }> = {
  pending: { label: 'Waiting for review', tone: 'amber' },
  accepted: { label: 'Accepted', tone: 'green' },
  rejected: { label: 'Rejected', tone: 'red' },
}

export default function OwnerContracts() {
  const queue = useGetOwnerContracts<Queue | null>()
  const [params] = useSearchParams()
  const [open, setOpen] = useState<string | null>(params.get('r'))
  const load = queue.trigger
  const refresh = useCallback(() => { void load({}, { skipCache: true }).catch(() => undefined) }, [load])
  useEffect(() => { refresh() }, [refresh])

  const rows = queue.data?.rows ?? []
  // Open the first waiting agreement on arrival: there is usually one, and it
  // is the reason the page was opened.
  useEffect(() => {
    if (open === null && rows.length && rows[0].status === 'awaiting_release') setOpen(rows[0].release_id)
  }, [rows, open])

  if (queue.loading && !queue.data && !queue.error) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading…</div>
  }
  if (queue.error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center text-sm">
          <p className="text-rose-700 dark:text-rose-300">{queue.error}</p>
          <Button size="sm" variant="outline" onClick={refresh}>Try again</Button>
        </div>
      </div>
    )
  }
  if (queue.data === null && !queue.loading) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">Owner contracts show identity numbers and bank details, so they are open to owner editors only.</p>
          <p className="text-xs text-muted-foreground">If you release owner agreements, ask an admin to switch on Edit owners for you in cockpit access.</p>
        </div>
      </div>
    )
  }

  const counts = queue.data?.counts
  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <FileSignature className="h-5 w-5" />
        </div>
        <div className="min-w-[12rem] flex-1">
          <h1 className="text-lg font-semibold text-foreground">Owner Contracts</h1>
          <p className="text-xs text-muted-foreground">
            Check the FICA documents from the contract form, accept each one, then release the agreement to sign.
          </p>
        </div>
        {counts && (
          <div className="flex flex-wrap gap-2">
            <Chip tone={counts.awaiting ? 'amber' : 'neutral'}>{counts.awaiting} waiting to release</Chip>
            <Chip tone="neutral">{counts.released_30d} released in 30 days</Chip>
          </div>
        )}
        <Button size="sm" variant="outline" onClick={refresh} disabled={queue.loading}>
          <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {rows.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">
            No agreement is waiting. One appears here when an owner submits the contract onboarding form.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => (
              <ReleaseItem key={r.release_id} r={r} open={open === r.release_id}
                onToggle={() => setOpen((x) => (x === r.release_id ? '' : r.release_id))} onChanged={refresh} />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function statusChip(r: Release) {
  if (r.status === 'released') return <Chip tone={r.signed_at ? 'green' : 'brand'}>{r.signed_at ? 'Signed' : 'Sent to sign'}</Chip>
  if (r.status === 'declined') return <Chip tone="red">Not sent</Chip>
  if (r.release_blockers.length === 0) return <Chip tone="green">Ready to release</Chip>
  return <Chip tone="amber">Documents to review</Chip>
}

function ReleaseItem({ r, open, onToggle, onChanged }: { r: Release; open: boolean; onToggle: () => void; onChanged: () => void }) {
  const accepted = r.documents.filter((d) => d.review_state === 'accepted').length
  return (
    <li>
      <button type="button" onClick={onToggle} className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-muted/40">
        {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
        <div className="min-w-[10rem] flex-1">
          <p className="text-sm font-medium text-foreground">{r.name ?? r.email ?? 'Owner'}</p>
          <p className="text-xs text-muted-foreground">
            {MODEL_LABEL[r.owner_model] ?? r.owner_model}. Form submitted {formatWhen(r.form_submitted_at ?? r.created_at)}.
          </p>
        </div>
        <span className="text-xs text-muted-foreground">
          {accepted} of {Math.max(r.form_files.length + r.form_files_unseen, new Set(r.documents.map((d) => d.doc_kind)).size)} documents accepted
        </span>
        {statusChip(r)}
      </button>
      {open && <ReleaseBody r={r} onChanged={onChanged} />}
    </li>
  )
}

function ReleaseBody({ r, onChanged }: { r: Release; onChanged: () => void }) {
  const waiting = r.status === 'awaiting_release'
  return (
    <div className="grid gap-4 border-t border-border bg-muted/20 px-4 py-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
      <FormAnswers r={r} />
      <div className="space-y-4">
        <Documents r={r} onChanged={onChanged} />
        {waiting ? <Decision r={r} onChanged={onChanged} /> : <Outcome r={r} />}
      </div>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string | boolean | null | undefined }) {
  // A missing answer shows as missing, never as blank or as "No".
  const shown = value === null || value === undefined || value === '' ? null : value === true ? 'Yes' : value === false ? 'No' : String(value)
  return (
    <div className="grid grid-cols-[9rem_minmax(0,1fr)] gap-2 py-0.5 text-xs">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={shown ? 'break-words text-foreground' : 'italic text-muted-foreground'}>{shown ?? 'not given'}</dd>
    </div>
  )
}

function FormAnswers({ r }: { r: Release }) {
  const d = r.details
  const juristic = d.entity_nature === 'A Juristic Person (Company / Trust etc.)'
  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-3">
      <div>
        <h2 className="text-sm font-semibold">What the owner told us on the form</h2>
        <p className="text-[11px] text-muted-foreground">Check the documents against these. A mismatch in the ID number, name or bank account is a reason to reject.</p>
      </div>
      <dl>
        <Field label="Email" value={r.email} />
        <Field label="Phone" value={r.phone ? localPhone(r.phone) : null} />
        <Field label="Contracting as" value={juristic ? 'A company or trust' : d.entity_nature ? 'An individual' : null} />
        {juristic && <Field label="Registered name" value={d.entity_registered_name} />}
        {juristic && <Field label="Registration no." value={d.entity_registration_number} />}
        <Field label="ID number" value={d.id_number} />
        <Field label="Date of birth" value={d.date_of_birth ? formatDay(String(d.date_of_birth)) : null} />
        <Field label="Address" value={d.address} />
        <Field label="Occupation" value={[d.occupation, d.employer].filter(Boolean).join(', ') || null} />
      </dl>
      <dl className="border-t border-border pt-2">
        <Field label="Bank" value={d.bank_name} />
        <Field label="Account holder" value={d.bank_account_name} />
        <Field label="Account number" value={d.bank_account_number} />
        <Field label="Branch code" value={d.bank_branch_code} />
      </dl>
      <dl className="border-t border-border pt-2">
        <Field label="Source of funds" value={d.source_of_funds} />
        <Field label="Detail" value={d.source_of_funds_detail} />
        <Field label="Own account" value={d.beneficial_owner_self} />
        {d.beneficial_owner_self === false && <Field label="Acting for" value={[d.beneficial_owner_name, d.beneficial_owner_id, d.beneficial_owner_relationship].filter(Boolean).join(', ')} />}
        <Field label="PEP" value={d.pep_declared} />
        {d.pep_declared === true && <Field label="PEP detail" value={d.pep_detail} />}
        <Field label="Sanctions consent" value={d.sanctions_consent} />
      </dl>
      {r.fica_complete === false && (
        <p className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
          The form is missing: {(r.fica_missing ?? []).join(', ').replace(/_/g, ' ') || 'reason not recorded'}.
        </p>
      )}
    </section>
  )
}

function Documents({ r, onChanged }: { r: Release; onChanged: () => void }) {
  const fetchFiles = useFetchOwnerFormFiles()
  const [busy, setBusy] = useState(false)
  const waiting = r.status === 'awaiting_release'
  // Each form file, with the document it became (if it did). A document a
  // person filed by hand for the same kind counts the same.
  const kinds = Array.from(new Set([...r.form_files.map((f) => f.doc_kind), ...r.documents.map((d) => d.doc_kind)]))
  const anyFailed = r.form_files.some((f) => f.status === 'failed' || f.status === 'pending') || r.form_files_unseen > 0

  async function refetch() {
    setBusy(true)
    try {
      await fetchFiles.trigger({ detail_id: r.onboarding_detail_id }).result
      toast.success('Fetching from the form', { description: 'The files appear here in under a minute.' })
      setTimeout(onChanged, 20000)
      setTimeout(onChanged, 60000)
    } catch (err) {
      toast.error('Could not fetch the files', { description: writeErrorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="space-y-2 rounded-lg border border-border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex-1 text-sm font-semibold">FICA documents</h2>
        {waiting && anyFailed && r.onboarding_detail_id && (
          <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" onClick={refetch} disabled={busy}>
            {busy ? <Spinner className="h-3.5 w-3.5" /> : <RefreshCw className="h-3.5 w-3.5" />} Fetch from the form
          </Button>
        )}
      </div>
      {r.form_files_unseen > 0 && r.form_files.length === 0 && (
        <p className="text-xs text-muted-foreground">
          {r.form_files_unseen} file{r.form_files_unseen === 1 ? '' : 's'} on the form {r.form_files_unseen === 1 ? 'is' : 'are'} being copied in. This happens on its own within 30 minutes; Fetch from the form does it now.
        </p>
      )}
      <ul className="space-y-2">
        {kinds.map((kind) => {
          const file = r.form_files.find((f) => f.doc_kind === kind)
          const docs = r.documents.filter((d) => d.doc_kind === kind)
          const label = file?.label ?? docs[0]?.label ?? kind
          return (
            <li key={kind} className="rounded-md border border-border p-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex-1 text-xs font-medium">{label}</span>
                {docs.length === 0 && file && file.status !== 'imported' && (
                  <Chip tone={file.status === 'pending' ? 'neutral' : 'red'}>{file.status === 'pending' ? 'Being copied in' : 'Could not copy'}</Chip>
                )}
              </div>
              {docs.length === 0 && file?.error && (
                <p className="mt-1 flex items-start gap-1 text-xs text-rose-700 dark:text-rose-300">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {file.error}
                </p>
              )}
              {docs.map((d) => <DocRow key={d.id} d={d} canAct={waiting} onChanged={onChanged} />)}
              {waiting && docs.every((d) => d.review_state !== 'accepted') && (
                <div className="mt-2">
                  <OwnerDocUpload applicationId={r.application_id} docKind={kind} onDone={onChanged}
                    label={docs.length ? 'Upload a better copy' : 'Upload it here'} />
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function DocRow({ d, canAct, onChanged }: { d: Doc; canAct: boolean; onChanged: () => void }) {
  const review = useReviewOwnerDocument()
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const st = STATE[d.review_state] ?? { label: d.review_state, tone: 'neutral' as ChipTone }

  async function decide(decision: 'accept' | 'reject') {
    setBusy(true)
    try {
      const out = (await review.trigger({ document_id: d.id, decision, reason: decision === 'reject' ? reason : null }).result) as { owner_emailed?: boolean } | null
      toast.success(decision === 'accept' ? 'Accepted' : 'Rejected', {
        description: decision === 'reject' && !out?.owner_emailed
          ? 'The owner has not been emailed. Tell them what to send instead, then upload it here.'
          : undefined,
      })
      setRejecting(false)
      setReason('')
      onChanged()
    } catch (err) {
      toast.error('Not done', { description: writeErrorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-2 space-y-1.5 border-t border-border pt-2 first:mt-1 first:border-0 first:pt-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" title={d.filename ?? undefined}>
          {d.filename ?? 'file'}, {d.uploaded_by_kind === 'typeform' ? 'from the form' : d.uploaded_by_kind === 'staff' ? 'uploaded by Fleetly' : d.uploaded_by_kind}
        </span>
        <Chip tone={st.tone}>{st.label}</Chip>
        <ViewButton id={d.id} />
        {canAct && d.review_state === 'pending' && !rejecting && (
          <>
            <Button size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => decide('accept')} disabled={busy}>
              <Check className="h-3.5 w-3.5" /> Accept
            </Button>
            <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" onClick={() => setRejecting(true)} disabled={busy}>
              <X className="h-3.5 w-3.5" /> Reject
            </Button>
          </>
        )}
      </div>
      {d.same_file_as && d.same_file_as.length > 0 && (
        <p className="text-xs text-amber-800 dark:text-amber-300">
          The owner sent this same file as their {d.same_file_as.join(' and ').toLowerCase()}. Check it really is both.
        </p>
      )}
      {d.review_state === 'accepted' && d.reviewer && <p className="text-[11px] text-muted-foreground">Accepted by {d.reviewer}, {formatWhen(d.reviewed_at)}</p>}
      {d.review_state === 'rejected' && <p className="text-xs text-rose-700 dark:text-rose-300">Rejected: {d.reject_reason}</p>}
      {rejecting && (
        <div className="flex flex-wrap items-center gap-2">
          <Input className="h-8 flex-1" autoFocus placeholder="Why, so the next person knows what to ask for" value={reason}
            onChange={(e) => setReason(e.target.value)} />
          <Button size="sm" className="h-8 text-xs" onClick={() => decide('reject')} disabled={busy || reason.trim().length < 5}>Reject</Button>
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setRejecting(false)}>Cancel</Button>
        </div>
      )}
      <ReadingView upload={d} onChanged={onChanged} />
    </div>
  )
}

function Decision({ r, onChanged }: { r: Release; onChanged: () => void }) {
  const decide = useDecideOwnerContract()
  const [mode, setMode] = useState<'release' | 'decline' | null>(null)
  const [reason, setReason] = useState('')
  const [ack, setAck] = useState(false)
  const [busy, setBusy] = useState(false)
  const ready = r.release_blockers.length === 0
  const ficaShort = r.fica_complete === false

  async function go(decision: 'release' | 'decline') {
    setBusy(true)
    try {
      await decide.trigger({ release_id: r.release_id, decision, reason: reason.trim() || null, acknowledge_incomplete_fica: ack }).result
      toast.success(decision === 'release' ? 'Agreement sent to sign' : 'Recorded as not sent', {
        description: decision === 'release'
          ? `SignNow emails ${r.email ?? 'the owner'} the agreement, then you countersign. Refresh in a minute to see it sent.`
          : undefined,
      })
      setMode(null)
      onChanged()
      if (decision === 'release') setTimeout(onChanged, 30000)
    } catch (err) {
      toast.error('Not done', { description: writeErrorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="space-y-2 rounded-lg border border-border bg-card p-3">
      <h2 className="text-sm font-semibold">Release the agreement</h2>
      {r.last_send_error && (
        <p className="rounded-md bg-rose-50 px-2 py-1.5 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">
          The last attempt to send it failed and nothing reached the owner: {r.last_send_error}
        </p>
      )}
      {ready ? (
        <p className="text-xs text-muted-foreground">
          Every document is accepted. Releasing emails {r.name ?? 'the owner'} the Vehicle Asset Management Agreement through SignNow, with their details filled in. You countersign after them. The vehicle schedule stays blank until the deposit is in and the car is bought.
        </p>
      ) : (
        <ul className="list-disc space-y-0.5 pl-4 text-xs text-amber-900 dark:text-amber-200">
          {r.release_blockers.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}
      {mode === null && (
        <div className="flex flex-wrap gap-2">
          <Button variant="brand" size="sm" disabled={!ready || busy} onClick={() => setMode('release')}>
            <Send className="mr-1 h-3.5 w-3.5" /> Release to sign
          </Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => setMode('decline')}>Do not send</Button>
        </div>
      )}
      {mode === 'release' && (
        <div className="space-y-2 rounded-md border border-border p-2">
          <p className="text-xs">Send the agreement to {r.name ?? 'the owner'} at {r.email ?? 'their email'} now?</p>
          {ficaShort && (
            <>
              <label className="flex items-start gap-2 text-xs">
                <input type="checkbox" className="mt-0.5" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                I am releasing it although the form is missing {(r.fica_missing ?? []).join(', ').replace(/_/g, ' ')}.
              </label>
              <Input className="h-8" placeholder="Why it can go anyway" value={reason} onChange={(e) => setReason(e.target.value)} />
            </>
          )}
          <div className="flex gap-2">
            <Button variant="brand" size="sm" onClick={() => go('release')} disabled={busy || (ficaShort && (!ack || reason.trim().length < 5))}>
              {busy ? <Spinner className="mr-1 h-3.5 w-3.5" /> : <Send className="mr-1 h-3.5 w-3.5" />} Yes, send it
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setMode(null)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}
      {mode === 'decline' && (
        <div className="space-y-2 rounded-md border border-border p-2">
          <Input className="h-8" autoFocus placeholder="Why it is not being sent. It stays on the owner's record." value={reason} onChange={(e) => setReason(e.target.value)} />
          <p className="text-[11px] text-muted-foreground">Nothing is emailed to the owner. Tell them yourself what happens next.</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => go('decline')} disabled={busy || reason.trim().length < 5}>Record as not sent</Button>
            <Button variant="ghost" size="sm" onClick={() => setMode(null)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}
    </section>
  )
}

function Outcome({ r }: { r: Release }) {
  return (
    <section className="space-y-1 rounded-lg border border-border bg-card p-3 text-xs">
      {r.status === 'released' && (
        <>
          <p>Released by {r.released_by ?? 'someone'} on {formatWhen(r.released_at)}.</p>
          <p className="text-muted-foreground">
            {r.signed_at ? `Signed ${formatWhen(r.signed_at)}.` : r.envelope_id ? 'Sent through SignNow, waiting for signatures.' : 'Waiting for SignNow to confirm it was sent.'}
          </p>
        </>
      )}
      {r.status === 'declined' && <p>Not sent. {r.declined_by ?? 'Someone'} recorded on {formatWhen(r.declined_at)}: {r.decline_reason}</p>}
    </section>
  )
}
